import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { AppShell } from '@/components/AppShell'
import { CatalogCard } from '@/components/CatalogCard'
import { api } from '@/lib/api'
import { catalog } from '@/lib/catalog'
import { Telemetry } from '@/lib/telemetry'
import type { Portfolio } from '@/types/api'

/**
 * Landing page for signed-in users.
 *
 * The IND list is driven by `commons/public/catalog.json` — the same
 * source the public landing page uses — so both surfaces stay visually
 * identical. The backend `/api/v1/projects` list is fetched only to map
 * each catalog entry to its concrete portfolio UUID so the card can
 * link into the live viewer (`/portfolio/:id/viewer`). The join key is
 * the FDA-assigned IND number (`ind_number` ↔ `Portfolio.indNumber`),
 * which is env-independent and survives project renames. Catalog
 * entries with no matching backend portfolio render unlinked.
 */
export function PortfolioListPage() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api.portfolios
      .list()
      .then((data) => {
        if (cancelled) return
        setPortfolios(data)
        Telemetry.portfolioListViewed({ portfolio_count: data.length })
      })
      .catch((err: Error) => {
        if (cancelled) return
        setError(err.message)
        Telemetry.errorSwallowed({
          error_type: 'api_failure',
          operation: 'portfolios_list',
          screen_name: '/',
          error_message: err.message ?? 'unknown',
        })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Strict join on the canonical FDA `ind_number`. Per-env data drift
  // is handled at the data layer now (migration 068 scoped the unique
  // index per-org so envs can carry the same canonical number without
  // collisions), not via a fallback in the UI.
  const portfolioByIndNumber = useMemo(() => {
    const map = new Map<string, Portfolio>()
    for (const p of portfolios) map.set(p.indNumber.toLowerCase(), p)
    return map
  }, [portfolios])

  const noPortfoliosProvisioned =
    !loading && !error && portfolios.length === 0

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-brand-charcoal mb-1">
            IND Portfolios
          </h1>
          <p className="text-sm text-slate-500">
            Browse submission packages from CTDCommons partner programs.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div className="mb-4 px-4 py-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
            Could not load portfolios: {error}
          </div>
        )}

        {noPortfoliosProvisioned && (
          <div className="mb-4 px-4 py-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600">
            Sign-in succeeded, but no portfolios are provisioned for your
            org yet. Cards below are previews from the public archive.
          </div>
        )}

        {!loading && (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {catalog.inds.map((ind) => {
              const portfolio = portfolioByIndNumber.get(
                ind.ind_number.toLowerCase(),
              )
              return (
                <li key={ind.slug}>
                  <CatalogCard
                    ind={ind}
                    compact
                    linkTo={
                      portfolio
                        ? `/portfolio/${portfolio.id}/viewer`
                        : undefined
                    }
                    onLinkClick={
                      portfolio
                        ? () =>
                            Telemetry.portfolioSelected({
                              portfolio_id: portfolio.id,
                            })
                        : undefined
                    }
                    telemetryPlacement="portfolio_list"
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </AppShell>
  )
}
